import { useSyncExternalStore } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  dateTime,
  PLACEHOLDER_BOUNDS,
  PLACEHOLDER_NOTICE,
  PROPERTIES,
  VOCABULARY_CONFIRMED,
} from '@kilroy/shared';

import { db } from '../db/db';
import { resetDevice } from '../boot';
import { useSession } from '../app/session';
import { formatBytes } from '../media/opfs';
import { getStatus, retryNow, subscribe } from '../sync/engine';

/* Settings, which on this app is mostly "prove to me my data is safe".
 *
 * Sign-out warns when the outbox is non-empty and refuses outright while media
 * is queued: the phone holds the only copy of those files, and signing out
 * would be the one action in the product that can destroy evidence.
 */

export function Settings() {
  const { deviceId, user, storage, standalone } = useSession();
  const status = useSyncExternalStore(subscribe, getStatus);
  const queued = useLiveQuery(() => db.outbox.count(), [], 0);
  const mediaQueued = status.mediaQueued;

  const pct = storage.quotaBytes
    ? Math.min(100, (storage.usageBytes / storage.quotaBytes) * 100)
    : 0;

  return (
    <div className="screen">
      <div className="screen__h">
        <h1 className="screen__t">Settings</h1>
        <p className="screen__s">{user.name} · {user.title}</p>
      </div>

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">Storage</h2>
          <span className="mod__n">{storage.persisted ? 'protected' : 'at risk'}</span>
        </div>
        <div className="card card--pad">
          {/* Storage as a bar, because "412 MB of 2.1 GB" is a fact you have to
              do arithmetic on and a bar is one you read. Ochre past 80%,
              because that is when eviction starts to matter. */}
          {storage.quotaBytes > 0 && (
            <div className={`stbar${pct > 80 ? ' stbar--tight' : ''}`}>
              <i style={{ width: `${Math.max(2, pct)}%` }} />
            </div>
          )}
          <div className="ed__facts">
            <span className="ed__key">Used</span>
            <span className="ed__val">
              {formatBytes(storage.usageBytes)}
              {storage.quotaBytes > 0 &&
                ` of ${formatBytes(storage.quotaBytes)} (${pct.toFixed(1)}%)`}
            </span>
            <span className="ed__key">Protected</span>
            <span className="ed__val">
              {storage.persisted ? 'yes — exempt from cleanup' : 'no — at risk of eviction'}
            </span>
            <span className="ed__key">Installed</span>
            <span className="ed__val">{standalone ? 'yes' : 'no — running in a browser tab'}</span>
            <span className="ed__key">Device</span>
            <span className="ed__val">{deviceId}</span>
          </div>
        </div>
      </section>

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">Offline packs</h2>
        </div>
        <div className="card card--pad">
          <ul className="packs">
            {PROPERTIES.map((p) => (
              <li key={p.id} className="pack">
                <div className="grow">
                  <strong>{p.name}</strong>
                  <div className="pack__meta">
                    ≈{p.pack_mb} MB · {p.jurisdiction}
                  </div>
                </div>
                <button type="button" className="btn btn--sand" disabled={PLACEHOLDER_BOUNDS}>
                  Download
                </button>
              </li>
            ))}
          </ul>
          {PLACEHOLDER_BOUNDS && <p className="notice">{PLACEHOLDER_NOTICE}</p>}
        </div>
      </section>

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">Sync</h2>
          <span className="mod__n">{status.pill}</span>
        </div>
        <div className="card card--pad">
          <div className="ed__facts">
            <span className="ed__key">State</span>
            <span className="ed__val">{status.pill}</span>
            <span className="ed__key">Last reached</span>
            <span className="ed__val">
              {status.lastSyncedAt ? dateTime(status.lastSyncedAt) : 'never'}
            </span>
            <span className="ed__key">Tag list</span>
            <span className="ed__val">
              {VOCABULARY_CONFIRMED ? 'confirmed' : 'draft — not signed off'}
            </span>
          </div>
          <button type="button" className="btn btn--quiet" onClick={() => void retryNow()}>
            Run connectivity probe
          </button>
        </div>
      </section>

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">Account</h2>
        </div>
        <div className="card card--pad">
          <button
            type="button"
            className="btn btn--quiet btn--lg"
            disabled={mediaQueued > 0}
            onClick={() => {
              if (queued > 0 && !confirm(`${queued} item(s) still queued. Sign out anyway?`))
                return;
              void resetDevice(false);
            }}
          >
            Sign out
          </button>
          {mediaQueued > 0 && (
            <p className="field__error">
              {mediaQueued} file{mediaQueued === 1 ? '' : 's'} still on this phone only. Sign-out
              is blocked until they reach the server.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
